import { useState } from 'react';
import { Configuration, SuppliersApi } from '@/api';
import useFindStoresQuery from '@/api/queries/useFindStoresQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import { SearchProps } from '@/components/search/useSearch';
import { Supplier } from '@/models/Supplier';
import { SupplierToStoreEditPath } from '@/modules/admin/paths';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

interface SupplierToStoreTableProps extends SearchProps {
  supplier: Supplier;
}

const SupplierToStoreTable = ({
  supplier,
  searchQuery,
  page = 1,
  onChangePage = () => {},
}: SupplierToStoreTableProps) => {
  const suppliersApi = new SuppliersApi(new Configuration(useAuth));

  const { stores, isLoading, itemsPerPage, invalidateQuery } = useFindStoresQuery(
    page,
    searchQuery,
    { supplierId: supplier.id },
  );

  const [deletingStoreId, setDeletingStoreId] = useState<string | null>(null);

  const handleDeleteStore = async (store: { id: string; name: string }) => {
    setDeletingStoreId(store.id);
    try {
      await suppliersApi.removeStoresFromSupplier(supplier.id, [{ id: store.id }]);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Store "${store.name}" removed successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery();
    } catch (error) {
      console.error('Failed to remove store from supplier:', error);
      toast.error('Failed to remove store.');
    } finally {
      setDeletingStoreId(null);
    }
  };

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <table className={isLoading ? 'loading' : ''}>
          <thead>
            <tr>
              <th>Store</th>
              <th>Customer Number</th>
              <th>MOQ</th>
              <th>Supplier</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && stores.items.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-6 text-gray-500">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <MagnifyingGlassIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
                    <span>No stores found</span>
                  </div>
                </td>
              </tr>
            ) : (
              stores.items.map((store) => {
                if (!store.id) return null;
                return (
                  <tr key={store.id}>
                    <td>
                      <Link
                        to={SupplierToStoreEditPath.withParams(supplier.id, store.id)}
                        className="text-blue-500 hover:underline"
                      >
                        {store.name}
                      </Link>
                    </td>
                    <td>{store?.customerCode}</td>
                    <td>{store?.moq}</td>
                    <td>{supplier.name}</td>
                    <td>
                      <Confirm
                        onConfirm={() => handleDeleteStore(store)}
                        title="Delete Store"
                        message={`Are you sure you want to remove the link between supplier "${supplier.name}" and store "${store.name}"?`}
                        danger
                      >
                        {(handleClick, loading) => (
                          <Button
                            onClick={handleClick}
                            icon={<XMarkIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
                            loading={loading || deletingStoreId === store.id}
                            variant="delete"
                            size="xs"
                          >
                            Delete
                          </Button>
                        )}
                      </Confirm>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {isLoading && (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading...</span>
          </div>
        )}
      </div>
      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={stores.totalCount || 0}
          estimatedTotalCount={stores.estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default SupplierToStoreTable;
